import React, { useEffect, useState } from "react";
import { Loader2, Newspaper, ArrowRight, Calendar } from "lucide-react";
import { LLink } from "@/i18n/LLink";
import { fetchPublishedNews, TYPE_LABELS } from "@/api/news";
import Seo from "@/components/Seo";
import { useLang } from "@/i18n/LangContext";

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

const imageUrl = (v) => {
  if (!v) return null;
  if (/^https?:\/\//i.test(v)) return v;
  if (v.startsWith("/api/")) return `${BACKEND_URL}${v}`;
  return v;
};

const formatDate = (iso, lang = "nl") => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(
      lang === "de" ? "de-DE" : lang === "en" ? "en-GB" : "nl-NL",
      { day: "numeric", month: "long", year: "numeric" },
    );
  } catch {
    return iso;
  }
};

export const NieuwsPage = () => {
  const { lang } = useLang();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [typeFilter, setTypeFilter] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchPublishedNews(typeFilter ? { type: typeFilter } : {});
        if (!cancelled) setItems(data.items || []);
      } catch (e) {
        if (!cancelled) setError(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [typeFilter]);

  return (
    <div data-testid="nieuws-page">
      <Seo
        title="Nieuws — Lemmer Leeft"
        description="Blog en persberichten van Lemmer Leeft. Ontdek wat er in Lemmer speelt."
        lang={lang}
      />
      {/* Hero */}
      <section className="relative py-16 sm:py-20 bg-gradient-to-b from-[#173A5E] to-[#0F2A46] text-white">
        <div className="ll-container">
          <span className="inline-flex items-center gap-2 text-[#6BC0E8] font-[Poppins] font-semibold text-xs uppercase tracking-[0.2em] mb-3">
            <Newspaper className="w-4 h-4" /> Nieuws & verhalen
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold font-[Poppins] leading-tight text-white">
            Lemmer in het nieuws
          </h1>
          <p className="mt-4 text-white/85 font-[Inter] text-base sm:text-lg max-w-2xl">
            Blogverhalen, tips en officiële persberichten van BIZ Vereniging Lemmer.
          </p>
        </div>
      </section>

      <section className="py-12 sm:py-16 bg-[#F4F8FB]">
        <div className="ll-container">
          <div className="flex flex-wrap items-center gap-3 mb-8">
            <button
              type="button"
              onClick={() => setTypeFilter("")}
              data-testid="nieuws-filter-all"
              className={`px-4 py-2 rounded-full text-sm font-[Poppins] font-semibold transition ${
                !typeFilter
                  ? "bg-[#173A5E] text-white"
                  : "bg-white text-[#173A5E] border border-[#E4EEF5] hover:border-[#6BC0E8]"
              }`}
            >
              Alles
            </button>
            <button
              type="button"
              onClick={() => setTypeFilter("blog")}
              data-testid="nieuws-filter-blog"
              className={`px-4 py-2 rounded-full text-sm font-[Poppins] font-semibold transition ${
                typeFilter === "blog"
                  ? "bg-[#173A5E] text-white"
                  : "bg-white text-[#173A5E] border border-[#E4EEF5] hover:border-[#6BC0E8]"
              }`}
            >
              Blog
            </button>
            <button
              type="button"
              onClick={() => setTypeFilter("persbericht")}
              data-testid="nieuws-filter-persbericht"
              className={`px-4 py-2 rounded-full text-sm font-[Poppins] font-semibold transition ${
                typeFilter === "persbericht"
                  ? "bg-[#173A5E] text-white"
                  : "bg-white text-[#173A5E] border border-[#E4EEF5] hover:border-[#6BC0E8]"
              }`}
            >
              Persberichten
            </button>
          </div>

          {loading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-[#2E9CD6]" />
            </div>
          ) : error ? (
            <p className="text-center text-[#8b1420] py-16">Kon berichten niet laden.</p>
          ) : items.length === 0 ? (
            <div className="bg-white rounded-2xl border border-[#E4EEF5] p-10 text-center">
              <p className="text-[#5A7594] font-[Inter]">Nog geen berichten gepubliceerd.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {items.map((it) => {
                const img = imageUrl(it.image);
                return (
                  <LLink
                    key={it.id}
                    to={`/nieuws/${it.slug}`}
                    data-testid={`nieuws-card-${it.slug}`}
                    className="group bg-white rounded-2xl border border-[#E4EEF5] overflow-hidden hover:shadow-2xl hover:-translate-y-0.5 transition-all"
                  >
                    <div className="aspect-[16/10] bg-[#F4F8FB] overflow-hidden">
                      {img ? (
                        <img
                          src={img}
                          alt={it.title}
                          loading="lazy"
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-[#8AA3B8]">
                          <Newspaper className="w-8 h-8" />
                        </div>
                      )}
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-3 mb-2 text-xs">
                        <span
                          className={`inline-block px-2 py-0.5 rounded-full font-semibold ${
                            it.type === "persbericht"
                              ? "bg-[#F4E9D8] text-[#8B6914]"
                              : "bg-[#E1F0FA] text-[#0F5C8A]"
                          }`}
                        >
                          {TYPE_LABELS[it.type] || it.type}
                        </span>
                        {it.published_at && (
                          <span className="inline-flex items-center gap-1 text-[#5A7594]">
                            <Calendar className="w-3 h-3" />
                            {formatDate(it.published_at, lang)}
                          </span>
                        )}
                      </div>
                      <h2 className="text-xl font-[Poppins] font-bold text-[#173A5E] leading-tight mb-2 group-hover:text-[#2E9CD6] transition-colors">
                        {it.title}
                      </h2>
                      {it.excerpt && (
                        <p className="text-sm text-[#3E5871] font-[Inter] line-clamp-3">
                          {it.excerpt}
                        </p>
                      )}
                      <span className="mt-4 inline-flex items-center gap-1 text-[#2E9CD6] font-[Poppins] font-semibold text-sm group-hover:gap-2 transition-all">
                        Lees verder
                        <ArrowRight className="w-4 h-4" />
                      </span>
                    </div>
                  </LLink>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default NieuwsPage;
